import { Bus } from './event.module';
import { AppEventKey, EventMap } from './models/event.types';

type EventKey = keyof EventMap;
export class PerformanceTracker {
  /**
   * Keep track of all labels that have been wired for measurement.
   */
  private trackedLabels = new Set<string>();
  private trackBetweenMap = new Map<string, () => void>();
  /**
   * Mark the start of a timing with the given label.
   */
  start(label: string) {
    performance.mark(`${label}:start`);
  }

  end(label: string) {
    performance.mark(`${label}:end`);
    try {
      performance.measure(label, `${label}:start`, `${label}:end`);
      performance.clearMarks(`${label}:start`);
      performance.clearMarks(`${label}:end`);
    } catch (e) {}
  }

  /**
   * Get all recorded durations (in ms) for a given label.
   * @param label - The measurement label
   * @param clearAfterRead - Whether to clear the measures after reading
   */
  getDurations(label: string, clearAfterRead = false): number[] {
    const measures = performance.getEntriesByName(
      label,
      'measure',
    ) as PerformanceMeasure[];
    const durations = measures.map((m) => m.duration);
    if (clearAfterRead) {
      performance.clearMeasures(label);
    }
    return durations;
  }

  /**
   * Return a mapping of all tracked labels to their durations.
   * @param clearAfterRead - Whether to clear the measures after reading
   */
  getAllDurations(clearAfterRead = false): Record<string, number[]> {
    const result: Record<string, number[]> = {};
    for (const label of this.trackedLabels) {
      result[label] = this.getDurations(label, clearAfterRead);
    }
    return result;
  }

  clearAllMeasures(): void {
    for (const label of this.trackedLabels) {
      performance.clearMeasures(label);
    }
  }

  getLastDuration(label: string, clear = false): number | undefined {
    const m = performance.getEntriesByName(
      label,
      'measure',
    ) as PerformanceMeasure[];
    const last = m.length && m[m.length - 1].duration;
    if (clear) performance.clearMeasures(label);
    return last;
  }

  trackBetween(startEvt: EventKey, endEvt: EventKey, label: string) {
    this.trackedLabels.add(label);

    const startEvent = Bus.on(startEvt, () => this.start(label));
    this.trackBetweenMap.set(`${label}:start`, startEvent);
    const endEvent = Bus.on(endEvt, () => this.end(label));
    this.trackBetweenMap.set(`${label}:end`, endEvent);
  }

  removeTrackBetween(label: string) {
    this.trackedLabels.delete(label);
    const startFn = this.trackBetweenMap.get(`${label}:start`);
    if (startFn) {
      startFn();
    }
    const endFn = this.trackBetweenMap.get(`${label}:end`);
    if (endFn) {
      endFn();
    }
  }

  trackBetweenDynamic(
    startEvt: EventKey,
    endEvt: EventKey,
    labelPrefix: string,
    keySelector: (payload: any) => string,
  ) {
    Bus.on(startEvt as any, (payload: any) => {
      const key = keySelector(payload);
      const label = `${labelPrefix}:${key}`;
      this.trackedLabels.add(label);
      this.start(label);
    });

    Bus.on(endEvt as any, (payload: any) => {
      const key = keySelector(payload);
      const label = `${labelPrefix}:${key}`;
      this.end(label);
    });
  }
}
